import React, { ReactComponentElement, useState, useEffect } from 'react'

import { Container, Content, ModalComponent, SubTitle, Title } from './styles'
import ButtonComp from '../../Button'
import useToast from '../../../hooks/useToast'
import Input from '../../Input'
import { useForm } from 'react-hook-form'
import { api } from '../../../services/api'
import { useDirectory } from '../../../store/directory'
import { Row } from '../../commons'
import { FaCircleXmark, FaFolder, FaFileLines } from 'react-icons/fa6'
import { ButtonIcon } from '../../Table/Pagination/styles'
// import FolderTree from 'react-folder-tree'
// import 'react-folder-tree/dist/style.css'
import { theme } from '../../../styles/theme'

export interface ModalHandle {
  openModal: () => void
  closeModal: () => void
}

export interface ModalProps {
  title?: string
  maxWidth?: number
  refetch: () => void
  onOpenModal?: () => void
  children?: ReactComponentElement<any>
  selectedItems?: any[]
}

export interface FileInterface {
  _id: any
  file: File
  name: string
  size: number
}

const ModalCreateFolder: React.ForwardRefRenderFunction<ModalHandle, ModalProps> = (
  { refetch, onOpenModal, maxWidth, selectedItems = [] },
  ref,
) => {
  const [isLoading, setisLoading] = React.useState(false)
  const [treeData, setTreeData] = useState<any>(null)
  const [selectedFolder, setSelectedFolder] = useState<any>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [allDocuments, setAllDocuments] = useState<any[]>([]) // Cache de todos os documentos
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set()) // Pastas expandidas
  const [folderChildren, setFolderChildren] = useState<Map<string, any[]>>(new Map()) // Cache de filhos de pastas

  const directory = useDirectory((state) => state.directory)

  const toast = useToast()

  const [open, setOpen] = React.useState(false)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: {
      name: '',
    },
  })

  // Função para converter dados para formato da árvore
  const convertToTreeData = (files: any[]): any[] => {
    if (!files || files.length === 0) {
      return [];
    }

    // Separar pastas e arquivos
    const folders = files.filter(file => file.isFolder);

    // Ordenar pastas em ordem alfabética
    const sortedFolders = folders.sort((a, b) => {
      return a.name.localeCompare(b.name, 'pt-BR', { 
        sensitivity: 'base',
        numeric: true 
      });
    });

    return sortedFolders.map(folder => ({
      name: folder.name,
      _id: folder._id,
      isFolder: true,
      children: []
    }));
  };

  // Carregar dados da árvore
  const loadTreeData = async () => {
    try {
      const response = await api.get('/documents', {
        params: {
          page: 1,
          perPage: 5000,
          directory: JSON.stringify(['/']),
        },
      });
      
      if (response.data?.data) {
        // Salvar todos os documentos para uso posterior
        setAllDocuments(response.data.data);
        
        const convertedData = convertToTreeData(response.data.data);
        setTreeData(convertedData);
      }
    } catch (error) {
      console.error('Erro ao carregar estrutura de pastas:', error);
    }
  };


  React.useImperativeHandle(ref, () => ({
    openModal() {
      reset()
      setSelectedFolder(null)
      setSelectedNodeId(null)
      setExpandedFolders(new Set()) // Clear expanded folders
      setFolderChildren(new Map()) // Clear folder children cache
      loadTreeData()
      setOpen(true)
    },
    closeModal() {
      setOpen(false)
    },
  }))

  const afterOpenModal = () => {
    if (onOpenModal) {
      onOpenModal()
    }
  }


  // Função para buscar documentos relacionados a uma pasta
  const loadRelatedDocuments = async (folderId: string, folderName: string) => {
    try {
      // Verificar se já temos os filhos em cache
      if (folderChildren.has(folderId)) {
        return
      }
      
      // Fazer uma nova busca na API especificamente para esta pasta
      const response = await api.get('/documents', {
        params: {
          page: 1,
          perPage: 5000,
          directory: JSON.stringify(['/', folderId]), // Buscar dentro da pasta específica
        },
      });
      
      if (response.data?.data) {
        // Filtrar apenas documentos que são pastas (isFolder = true) e ordenar alfabeticamente
        const relatedFolders = response.data.data
          .filter((doc: any) => doc.isFolder === true)
          .sort((a: any, b: any) => {
            return a.name.localeCompare(b.name, 'pt-BR', { 
              sensitivity: 'base',
              numeric: true 
            });
          })
        
        // Salvar no cache
        setFolderChildren(prev => new Map(prev).set(folderId, relatedFolders))
      }
      
    } catch (error) {
      console.error('Erro ao buscar documentos relacionados:', error)
    }
  }

  // Função para lidar com clique na árvore
  const onNameClick = async (nodeData: any) => {
    if (nodeData.isFolder) {
      setSelectedFolder(nodeData);
      setSelectedNodeId(nodeData._id);
      
      // Alternar expansão da pasta
      const isExpanded = expandedFolders.has(nodeData._id);
      if (isExpanded) {
        // Recolher pasta
        setExpandedFolders(prev => {
          const newSet = new Set(prev);
          newSet.delete(nodeData._id);
          return newSet;
        });
      } else {
        // Expandir pasta
        setExpandedFolders(prev => new Set(prev).add(nodeData._id));
        // Carregar documentos relacionados passando o nome da pasta
        await loadRelatedDocuments(nodeData._id, nodeData.name);
      }
    }
  };


  // Componente de item da árvore com expansão
  const TreeNode = ({ folder, level = 0 }: { folder: any, level?: number }) => {
    const isSelected = selectedNodeId === folder._id;
    const isExpanded = expandedFolders.has(folder._id);
    const children = folderChildren.get(folder._id) || [];
    
    return (
      <div style={{ marginLeft: `${level * 20}px` }}>
        <div
          style={{
            padding: '12px 16px',
            margin: '4px 0',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backgroundColor: isSelected 
              ? theme.colors.primary 
              : 'transparent',
            color: isSelected ? 'white' : theme.colors.primary,
            fontWeight: isSelected ? '600' : '500',
            border: isSelected 
              ? `2px solid ${theme.colors.primary}` 
              : '2px solid transparent',
            boxShadow: isSelected 
              ? `0 6px 20px ${theme.colors.primary}40` 
              : '0 2px 8px rgba(0,0,0,0.04)',
            transform: isSelected ? 'translateX(8px) translateY(-2px)' : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
          onClick={() => onNameClick(folder)}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = `${theme.colors.primary}15`;
              e.currentTarget.style.transform = 'translateX(4px) translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${theme.colors.primary}25`;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {children.length > 0 && (
              <span style={{
                color: isSelected ? 'white' : theme.colors.primary,
                fontSize: '12px',
                fontWeight: 'bold',
                width: '16px',
                textAlign: 'center'
              }}>
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            <FaFolder 
              style={{ 
                color: isSelected ? 'white' : theme.colors.primary,
                fontSize: '16px',
                opacity: 0.8
              }} 
            />
          </div>
          <span style={{
            color: isSelected ? 'white' : theme.colors.primary,
            fontWeight: isSelected ? '600' : '500',
            fontSize: '14px'
          }}>
            {folder.name}
          </span>
          {children.length > 0 && (
            <span style={{
              color: isSelected ? 'white' : theme.colors.primary,
              fontSize: '12px',
              opacity: 0.7,
              marginLeft: 'auto'
            }}>
              ({children.length})
            </span>
          )}
        </div>
        
        {/* Renderizar filhos se a pasta estiver expandida */}
        {isExpanded && children.length > 0 && (
          <div style={{ marginLeft: '20px' }}>
            {children.map((child: any, index: number) => (
              <TreeNode 
                key={child._id || index} 
                folder={child} 
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };


  // Função para mover pasta/documento
  const handleMove = async () => {
    try {
      setisLoading(true)

      if (!selectedFolder) {
        return toast.errorToast('Selecione uma pasta de destino!');
      }

      if (selectedItems.length === 0) {
        toast.errorToast('Nenhum item selecionado para mover!')
        return
      }

      // Preparar dados para a API
      const documentIds = selectedItems.map(item => item._id);

      // Chamar a nova API de movimentação
      const response = await api.post('/documents/move', {
        documentIds,
        destinationFolderId: selectedFolder._id
      });

      if (response.data.success) {
        const { successful, failed, total } = response.data.results;
        
        if (successful.length > 0) {
          toast.successToast(`${successful.length} de ${total} item(s) movido(s) com sucesso para '${selectedFolder.name}'!`);
        }
        
        if (failed > 0) {
          toast.errorToast(`${failed} item(s) não puderam ser movidos!`);
        }
      } else {
        toast.errorToast('Erro ao mover itens!');
      }

      setOpen(false)
      if (refetch) refetch()
    } catch (error) {
      console.error('Erro ao mover pasta/documento:', error)
      toast.errorToast('Erro ao mover pasta/documento!');
    } finally {
      setisLoading(false)
    }
  }

  return (
    <ModalComponent
      isOpen={open}
      onAfterOpen={afterOpenModal}
      onRequestClose={() => setOpen(false)}
      contentLabel='Example Modal'
    >
      <Container>
        <Content maxWidth={maxWidth}>
          <Row justify='space-between' style={{ marginBottom: '24px' }}>
            <div>
              <Title style={{ 
                fontSize: '24px', 
                fontWeight: '700',
                color: theme.colors.primary,
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: `${theme.colors.primary}15`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  📁
                </div>
                Mover Pasta/Documento
              </Title>
              <SubTitle style={{ 
                fontSize: '14px', 
                color: '#666',
                fontWeight: '400',
                margin: 0
              }}>
                Selecione a pasta de destino para mover
              </SubTitle>
            </div>

            {selectedItems.length > 0 && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '12px', 
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  fontWeight: '600',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📁 Itens Selecionados ({selectedItems.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedItems.map((item, index) => (
                    <div
                      key={item._id || index}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: theme.colors.primary + '15',
                        color: theme.colors.primary,
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {item.isFolder ? '📁' : '📄'}
                      {item.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <ButtonIcon 
              onClick={() => setOpen(false)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: '#f5f5f5',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <FaCircleXmark size={20} color="#666" />
            </ButtonIcon>
          </Row>

                 {selectedFolder && (
                   <div style={{ 
                     padding: '16px', 
                     backgroundColor: `${theme.colors.primary}15`, 
                     borderRadius: '12px', 
                     marginBottom: '20px',
                     border: `2px solid ${theme.colors.primary}30`,
                     boxShadow: `0 2px 8px ${theme.colors.primary}20`,
                     display: 'flex',
                     alignItems: 'center',
                     gap: '12px'
                   }}>
                     <div style={{
                       width: '40px',
                       height: '40px',
                       backgroundColor: theme.colors.primary,
                       borderRadius: '50%',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       color: 'white',
                       fontSize: '18px'
                     }}>
                       📁
                     </div>
                     <div>
                       <div style={{ 
                         fontSize: '12px', 
                         color: theme.colors.primary, 
                         fontWeight: '600',
                         textTransform: 'uppercase',
                         letterSpacing: '0.5px'
                       }}>
                         Pasta Selecionada
                       </div>
                       <div style={{ 
                         fontSize: '16px', 
                         fontWeight: 'bold',
                         color: theme.colors.primary,
                         marginTop: '2px'
                       }}>
                         {selectedFolder.name}
                       </div>
                     </div>
                   </div>
                 )}


          {treeData && (
            <div style={{ 
              height: '350px', 
              overflow: 'auto', 
              border: `2px solid ${theme.colors.primary}20`, 
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '8px',
                left: '20px',
                right: '20px',
                height: '2px',
                backgroundColor: `${theme.colors.primary}20`,
                borderRadius: '1px',
                zIndex: 1
              }} />
              <div style={{
                position: 'absolute',
                top: '8px',
                left: '20px',
                height: '2px',
                backgroundColor: theme.colors.primary,
                borderRadius: '1px',
                zIndex: 2,
                width: '0%',
                transition: 'width 0.3s ease'
              }} />
              <div style={{ padding: '8px 0' }}>
                {treeData && treeData.length > 0 ? (
                  treeData.map((folder: any, index: number) => (
                    <TreeNode 
                      key={folder._id || index} 
                      folder={folder} 
                      level={0}
                    />
                  ))
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    Nenhuma pasta encontrada
                  </div>
                )}
              </div>
              <style>{`
                /* Scroll personalizado */
                ::-webkit-scrollbar {
                  width: 8px;
                }
                ::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 4px;
                }
                ::-webkit-scrollbar-thumb {
                  background: ${theme.colors.primary}40;
                  border-radius: 4px;
                  transition: background 0.3s ease;
                }
                ::-webkit-scrollbar-thumb:hover {
                  background: ${theme.colors.primary}60;
                }
              `}</style>
            </div>
          )}

                 <div style={{ 
                   display: 'flex', 
                   gap: '12px', 
                   marginTop: '8px' 
                 }}>
                   <ButtonComp
                     loading={isLoading}
                     color='primary'
                     onClick={handleMove}
                     variantType='block'
                     fullWidth
                     label='Mover Pasta/Documento'
                   />
                 </div>
        </Content>
      </Container>
    </ModalComponent>
  )
}

export default React.forwardRef(ModalCreateFolder)
